import { Fragment } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import type { TipTapNode, TipTapRenderOptions } from './types.js';

export function renderTextNode(node: TipTapNode, options: TipTapRenderOptions, key: string): ReactNode {
  const text = node.text ?? '';
  if (!node.marks?.length) return <Fragment key={key}>{text}</Fragment>;
  let current: ReactNode = text;
  for (const mark of node.marks) {
    current = wrapMark(mark, current, options, key);
  }
  return <Fragment key={key}>{current}</Fragment>;
}

function wrapMark(
  mark: { type: string; attrs?: Record<string, unknown> },
  child: ReactNode,
  options: TipTapRenderOptions,
  key: string,
): ReactNode {
  switch (mark.type) {
    case 'bold':
      return <strong key={`${key}-b`}>{child}</strong>;
    case 'italic':
      return <em key={`${key}-i`}>{child}</em>;
    case 'underline':
      return <u key={`${key}-u`}>{child}</u>;
    case 'strike':
      return <s key={`${key}-s`}>{child}</s>;
    case 'subscript':
      return <sub key={`${key}-sub`}>{child}</sub>;
    case 'superscript':
      return <sup key={`${key}-sup`}>{child}</sup>;
    case 'code':
      return <InlineCode key={`${key}-c`} color={options.color}>{child}</InlineCode>;
    case 'link':
      return <LinkMark key={`${key}-l`} href={mark.attrs?.['href'] as string | undefined} color={options.primary}>{child}</LinkMark>;
    case 'textStyle':
      return renderTextStyle(mark.attrs, child, key);
    case 'color': {
      const color = mark.attrs?.['color'] as string | undefined;
      if (color) return <span key={`${key}-color`} style={{ color }}>{child}</span>;
      return child;
    }
    case 'fontSize': {
      const size = mark.attrs?.['size'] ?? mark.attrs?.['fontSize'];
      if (size) return <span key={`${key}-size`} style={{ fontSize: normalizeSize(size) }}>{child}</span>;
      return child;
    }
    case 'fontFamily': {
      const family = mark.attrs?.['fontFamily'] as string | undefined;
      if (family) return <span key={`${key}-font`} style={{ fontFamily: family }}>{child}</span>;
      return child;
    }
    case 'highlight': {
      const highlight = (mark.attrs?.['color'] as string | undefined) ?? '#fef3c7';
      return <mark key={`${key}-h`} style={{ background: highlight, padding: '0 2px', borderRadius: 3 }}>{child}</mark>;
    }
    default:
      return child;
  }
}

function renderTextStyle(attrs: Record<string, unknown> | undefined, child: ReactNode, key: string): ReactNode {
  if (!attrs) return child;
  const style: CSSProperties = {};
  const color = attrs['color'] as string | undefined;
  const fontFamily = attrs['fontFamily'] as string | undefined;
  const fontSize = attrs['fontSize'] ?? attrs['size'];
  if (color) style.color = color;
  if (fontFamily) style.fontFamily = fontFamily;
  if (fontSize) style.fontSize = normalizeSize(fontSize);
  if (Object.keys(style).length === 0) return child;
  return <span key={`${key}-ts`} style={style}>{child}</span>;
}

function normalizeSize(value: unknown): string | number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return `${trimmed}px`;
  return trimmed;
}

function InlineCode({ children, color }: { children: ReactNode; color: string }): JSX.Element {
  return (
    <code
      style={{
        background: `${color}14`,
        padding: '2px 6px',
        borderRadius: 4,
        fontSize: '0.9em',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
      }}
    >
      {children}
    </code>
  );
}

function LinkMark({ children, href, color }: { children: ReactNode; href: string | undefined; color: string }): JSX.Element {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color, textDecoration: 'underline' }}>
      {children}
    </a>
  );
}
